import React, { useEffect, useState, useRef } from "react";
import API from "../../../services/api";
import { formatDateDDMMYYY as formatDate } from "../../../utils/dateFormat";

const LIMIT = 5;

/**
 * Campaign Info tab – shows campaign cards with infinite scroll.
 *
 * Props (managed by parent FilterBar):
 *   month       – "YYYY-MM" string
 *   salesPerson – { value, label } | null
 *   brand       – { value, label } | null
 */
const CampaignInfo = ({ month, salesPerson, brand }) => {
    const [campaignInfo, setCampaignInfo] = useState([]);
    const [newsPaperData, setNewsPaperData] = useState([]);
    const [loader, setLoader] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    // Use refs so fetchCampaignInfo always reads the live values even from stale closures.
    const pageRef = useRef(1);
    const hasMoreRef = useRef(true);
    const isFetchingRef = useRef(false);
    const sentinelRef = useRef(null);

    const getLogo = (target) =>
        newsPaperData.find((item) => item.name === target)?.logo;

    // Fetch newspapers once on mount
    useEffect(() => {
        const fetchNewsPapers = async () => {
            try {
                const res = await API({
                    url: "/market/newspapers",
                    method: "POST",
                    responseType: "json",
                    data: {},
                });
                if (res.code === 200) setNewsPaperData(res.data);
            } catch (err) {
                console.error("Error fetching newspapers:", err);
            }
        };
        fetchNewsPapers();
    }, []);

    const fetchCampaignInfo = async (reset = false) => {
        if (!hasMoreRef.current && !reset) return;
        if (isFetchingRef.current) return; // prevent concurrent fetches

        isFetchingRef.current = true;
        const currentPage = reset ? 1 : pageRef.current;

        if (reset) setLoader(true);
        else setLoadingMore(true);

        try {
            const res = await API({
                url: "/user/campaign_creation/getCampaignInfo",
                method: "POST",
                responseType: "json",
                data: {
                    brand_id: brand?.value ?? -1,
                    month: new Date(month + "-01"),
                    sales_person_id: salesPerson?.value ?? -1,
                    page: currentPage,
                    limit: LIMIT,
                },
            });
            if (res.code === 200) {
                const newData = res.data;
                const more = newData.length === LIMIT;
                setCampaignInfo((prev) => (reset ? newData : [...prev, ...newData]));
                hasMoreRef.current = more;
                setHasMore(more);
                // Always advance so the next load fetches the correct page.
                pageRef.current = currentPage + 1;
            }
        } catch (err) {
            console.error("Error fetching campaign info:", err);
        } finally {
            isFetchingRef.current = false;
            setLoader(false);
            setLoadingMore(false);
        }
    };

    // Reset and refetch when filters change
    useEffect(() => {
        pageRef.current = 1;
        hasMoreRef.current = true;
        setHasMore(true);
        setCampaignInfo([]);
        fetchCampaignInfo(true);
    }, [month, salesPerson, brand]); // eslint-disable-line react-hooks/exhaustive-deps

    // Infinite scroll observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !loader && !loadingMore && hasMore) {
                    fetchCampaignInfo();
                }
            },
            { threshold: 0 }
        );
        if (sentinelRef.current) observer.observe(sentinelRef.current);
        return () => {
            if (sentinelRef.current) observer.unobserve(sentinelRef.current);
        };
    }, [loader, loadingMore, hasMore]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="p-4">
            {loader && (
                <div className="text-center py-8 text-sm text-gray-500">Loading…</div>
            )}

            {!loader && campaignInfo.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {campaignInfo.map((campaign, idx) => (
                        <div
                            key={idx}
                            className="rounded-lg border border-gray-200 shadow-sm p-4 flex flex-col gap-3 bg-white hover:shadow-md transition-shadow"
                        >
                            {/* Brand + Sales Person */}
                            <div className="flex flex-col gap-1">
                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                    Brand
                                </span>
                                <span className="text-sm font-semibold text-gray-900">
                                    {campaign.brand_name}
                                </span>
                                <span className="text-sm text-gray-600">
                                    {campaign.sales_person_name}
                                </span>
                            </div>

                            <div className="border-t border-gray-100" />

                            {/* Dates */}
                            <div className="flex justify-between gap-2 text-xs">
                                <div className="flex flex-col gap-0.5">
                                    <span className="text-gray-500 font-medium">Campaign Date</span>
                                    <span className="text-gray-800 font-semibold">
                                        {formatDate(campaign.campaign_date)}
                                    </span>
                                </div>
                                <div className="flex flex-col gap-0.5 text-right">
                                    <span className="text-gray-500 font-medium">Info Given</span>
                                    <span className="text-gray-800 font-semibold">
                                        {formatDate(campaign.reg_date)}
                                    </span>
                                </div>
                            </div>

                            {/* Target Platforms */}
                            {campaign.target_platform?.length > 0 && (
                                <div className="flex flex-col gap-1">
                                    <span className="text-xs font-medium text-gray-500">
                                        Platforms
                                    </span>
                                    <div className="flex flex-wrap gap-2">
                                        {campaign.target_platform.map((target) => (
                                            <img
                                                key={target}
                                                src={getLogo(target)}
                                                alt={target}
                                                style={{ maxWidth: "40px", height: "auto" }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                !loader && (
                    <div className="text-center py-12 text-gray-400 text-sm">
                        No campaign data found
                    </div>
                )
            )}

            {/* Infinite scroll sentinel */}
            {hasMore && !loader && (
                <div ref={sentinelRef} className="text-center py-4 text-sm text-gray-400">
                    {loadingMore ? "Loading more…" : ""}
                </div>
            )}
        </div>
    );
};

export default CampaignInfo;
